import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { singIn } from "../../../../redux/reduxApi/SingIn&SingUp/SinginApi";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { singUp } from "../../../../redux/reduxApi/SingIn&SingUp/SingUpApi";
import { Link, useNavigate } from "react-router-dom";

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    email: "",
    password: "",
    remember: false,
  });
  const dispatch = useDispatch();
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);

    try {
      const response = isLogin ? await dispatch(singIn(formData)) : await dispatch(singUp(formData));
      console.log("Signin/Signup Response:", response);

      if (response.payload.data.success) {
        toast.success(response.payload.data.message);

        if (isLogin) {
          // On login, redirect to home or stored redirect path
          const redirectPath = localStorage.getItem("redirect");
          redirectPath ? navigate(redirectPath) : navigate("/");
          window.location.reload();
        } else {
          // On signup, go to signup OTP page
          navigate(`/singup-otp/${formData.email}`);
        }
      } else {
        toast.error(response?.payload?.data?.message || "Something went wrong");
      }
    } catch (error) {
      console.log(error);
      toast.error("Login failed");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-50 via-white to-blue-50 p-4 md:p-10">
      <motion.div
        className="bg-white shadow-2xl rounded-3xl overflow-hidden w-full max-w-6xl flex flex-col md:flex-row transition-all duration-700"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Left Column */}
        <div className="hidden md:flex flex-col justify-between bg-gradient-to-br from-[#0e477e] to-[#1a3964] text-white p-8 w-1/2">
          <div className="flex flex-col h-full justify-between relative z-10">
            <div>
              <div className="flex items-center mb-8">
                <motion.div className="bg-white text-[#0e477e] font-bold text-2xl px-3 py-1 rounded-lg mr-3 shadow-md" whileHover={{ rotate: [0, -5, 5, 0] }} transition={{ duration: 0.5 }}>
                  T
                </motion.div>
                <h1 className="ml-3 text-3xl font-bold tracking-wide">Trukrma 🚛</h1>
              </div>

              <motion.h2 className="text-3xl md:text-4xl font-bold mb-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                <h2 className="text-4xl font-bold mb-4 leading-tight">Welcome to Trukrma 👋</h2>
              </motion.h2>

              <motion.p className="text-lg md:text-xl mb-8 text-blue-100" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
                Your one-stop destination for premium products at unbeatable prices.
              </motion.p>

              <motion.ul className="space-y-3" initial="hidden" animate="visible">
                {["Exclusive member discounts", "Fast & secure checkout", "24/7 customer support"].map((item, index) => (
                  <motion.li key={index} className="flex items-center text-sm md:text-base">
                    <motion.div className="mr-3" whileHover={{ scale: 1.1 }}>
                      <svg className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </motion.div>
                    {item}
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="w-full md:w-1/2 p-6 md:p-10">
          {/* Toggle Login/Signup */}
          <div className="flex justify-center mb-6">
            <div className="bg-gray-200 p-1 rounded-full flex w-full max-w-md relative">
              <motion.button onClick={() => setIsLogin(true)} className={`w-1/2 py-2 z-10 font-semibold transition-all ${isLogin ? "text-white" : "text-gray-700"}`}>
                Login 🔐
              </motion.button>
              <motion.button onClick={() => setIsLogin(false)} className={`w-1/2 py-2 z-10 font-semibold transition-all ${!isLogin ? "text-white" : "text-gray-700"}`}>
                Sign Up 📝
              </motion.button>
              <motion.div
                layout
                className="absolute top-1 bottom-1 left-1 w-1/2 bg-gradient-to-r from-orange-400 to-amber-500 rounded-full shadow-lg"
                animate={{ x: isLogin ? 0 : "100%" }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              />
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <AnimatePresence mode="wait">
              {!isLogin && (
                <motion.div key="name" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4 }}>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Name 🧑
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email 📧
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password 🔑
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>

            {isLogin && (
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" name="remember" checked={formData.remember} onChange={handleChange} className="accent-amber-500" />
                  <span>Remember me 🧠</span>
                </label>
                <Link className="text-blue-600 hover:underline">Forgot password?</Link>
              </div>
            )}

            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              type="submit"
              className="w-full py-3 rounded-xl bg-gradient-to-r from-orange-400 to-amber-500 text-white font-semibold shadow-lg transition-transform"
            >
              {isLogin ? "Login 🔓" : "Sign Up 🆕"}
            </motion.button>
          </form>
          {/* Bottom switch */}
          <div className="mt-6 text-center text-sm">
            {isLogin ? "Don’t have an account?" : "Already have an account?"}{" "}
            <button onClick={() => setIsLogin(!isLogin)} className="text-blue-600 font-medium hover:underline">
              {isLogin ? "Sign up here" : "Login here"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AuthPage;
